/**
 * Where to send a visitor after they sign in (FR-1.4).
 *
 * React Router carries the intended route in `location.state`, which lives in
 * memory and works for a navigation inside the app. It does not survive
 * `loginRedirect`: the browser leaves for Entra entirely, comes back to the
 * origin, and the state is gone — so a shared link to a book landed everybody
 * on the home page. That is what this fixes.
 *
 * `sessionStorage`, to match the MSAL cache: the same tab, the same session,
 * nothing left behind afterwards.
 */

const KEY = 'itpu.library.returnTo'

/** Remembered just before leaving for the identity provider. */
export function rememberReturnTo(path: string): void {
  try {
    if (isInternalPath(path)) sessionStorage.setItem(KEY, path)
  } catch {
    // Private browsing, or site data blocked. The visitor lands on the home
    // page — the old behaviour, and not worth failing a sign-in over.
  }
}

/** Reads it once and forgets it, so a later sign-in does not reuse a stale route. */
export function takeReturnTo(): string | null {
  try {
    const path = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    return path !== null && isInternalPath(path) ? path : null
  } catch {
    return null
  }
}

/**
 * Only a path inside this app.
 *
 * Validated on the way in *and* on the way out: a value read back from storage
 * is not evidence that we wrote it, and a stored "//evil.example" would
 * otherwise become an off-site redirect the moment somebody signs in.
 */
function isInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\')
}
